import React, { useState, useEffect } from 'react';
import VaultSidebar from '../../components/vault/VaultSidebar';
import VaultHeader from '../../components/vault/VaultHeader';
import { PlusIcon, BriefcaseIcon, UsersIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from '../../hooks/useRedux';
import { hasPermission } from '../../utils/permissionUtils';
import RoleList from '../../components/roles/RoleList';
import RoleModal from '../../components/roles/RoleModal';
import { RolesService } from '../../services/roles.service';
import { IRole } from '../../types/roles.types';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import HeroBanner from '../../components/common/HeroBanner';

const RolesPage: React.FC = () => {
    // States for Roles functionality
    const [allRoles, setAllRoles] = useState<IRole[]>([]);
    const [selectedRole, setSelectedRole] = useState<IRole | null>(null);
    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
    const [rolesError, setRolesError] = useState<string | null>(null);
    const [rolesLoading, setRolesLoading] = useState(false);
    const navigate = useNavigate();

    const { permissions, team_id } = useAppSelector(state => state.auth);
    const hasManageRolesPermission = hasPermission(permissions, 'role_management', 'view_roles');

    const fetchRoles = async () => {
        try {
            setRolesLoading(true);
            const response = await RolesService.getRoles(team_id || '');
            setAllRoles(response || []);
            setRolesError(null);
        } catch (error) {
            console.error('Error fetching roles:', error);
            setRolesError('Failed to load roles');
        } finally {
            setRolesLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, [team_id]);

    const handleNavigateToTeams = () => {
        navigate('/teams');
    };

    const handleNavigateToMembers = () => {
        navigate('/manage-members');
    };

    if (!hasManageRolesPermission) {
        return (
            <div className="h-screen flex overflow-hidden bg-gray-100">
                <VaultSidebar />
                <div className="flex flex-col flex-1 overflow-hidden">
                    <VaultHeader />
                    <main className="flex-1 relative overflow-y-auto focus:outline-none">
                        <div className="py-6">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                                <div className="text-center py-12">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        You don't have permission to view roles.
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen flex overflow-hidden bg-gray-100">
            <VaultSidebar />
            <div className="flex flex-col flex-1 overflow-hidden">
                <VaultHeader />
                <main className="flex-1 relative overflow-y-auto focus:outline-none bg-gray-50 dark:bg-gray-900">
                    {/* Hero Banner */}
                    <HeroBanner
                        title="Role Management"
                        description="Define and manage user permissions and access levels"
                        icon={<ShieldCheckIcon className="w-8 h-8 text-white" />}
                        variant="roles"
                    >
                        {/* Tab Navigation */}
                        <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-2xl p-1 sm:p-1.5 shadow-lg border border-white/30 overflow-x-auto">
                            {hasPermission(permissions, 'team', 'view_teams') && (
                                <button
                                    onClick={handleNavigateToTeams}
                                    className="flex items-center px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 text-white/80 hover:text-white hover:bg-white/20 whitespace-nowrap"
                                >
                                    <BriefcaseIcon className="h-5 w-5 sm:mr-2" />
                                    <span className="hidden sm:inline">Teams</span>
                                </button>
                            )}
                            {hasPermission(permissions, 'team', 'view_team_members') && (
                                <button
                                    onClick={handleNavigateToMembers}
                                    className="flex items-center px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 text-white/80 hover:text-white hover:bg-white/20 whitespace-nowrap"
                                >
                                    <UsersIcon className="h-5 w-5 sm:mr-2" />
                                    <span className="hidden sm:inline">Team Members</span>
                                </button>
                            )}
                            {hasPermission(permissions, 'role_management', 'view_roles') && (
                                <button
                                    className="flex items-center px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 bg-white text-primary-600 shadow-lg whitespace-nowrap"
                                >
                                    <ShieldCheckIcon className="h-5 w-5 sm:mr-2" />
                                    <span className="hidden sm:inline">Roles</span>
                                </button>
                            )}
                        </div>
                    </HeroBanner>

                    {/* Content Section */}
                    <div className="px-6 py-8">
                        <div className="w-full">
                            {rolesLoading ? (
                                <div className="mt-6 flex justify-center">
                                    <div className="animate-pulse flex space-x-4">
                                        <div className="flex-1 space-y-6 py-1">
                                            <div className="h-40 bg-gray-200 rounded"></div>
                                            <div className="space-y-3">
                                                <div className="h-4 bg-gray-200 rounded"></div>
                                                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <RoleList
                                    setSelectedRole={setSelectedRole}
                                    setIsModalOpen={setIsRoleModalOpen}
                                    roles={allRoles}
                                    loading={rolesLoading}
                                    error={rolesError}
                                    setRoles={setAllRoles}
                                />
                            )}
                        </div>
                    </div>

                    {/* Floating Action Button */}
                    {hasPermission(permissions, 'role_management', 'create_roles') && (
                        <button
                            onClick={() => {
                                setSelectedRole(null);
                                setIsRoleModalOpen(true);
                            }}
                            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 bg-primary-600 hover:bg-primary-700 text-white rounded-full p-3 sm:p-4 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center justify-center z-10"
                            aria-label="Add Role"
                        >
                            <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                            <span className="ml-2 hidden sm:inline">Add Role</span>
                        </button>
                    )}
                </main>
            </div>

            {isRoleModalOpen && (
                <RoleModal
                    isOpen={isRoleModalOpen}
                    role={selectedRole}
                    onClose={() => {
                        setIsRoleModalOpen(false);
                        setSelectedRole(null);
                    }}
                    onRoleChange={() => {
                        fetchRoles();
                    }}
                />
            )}
        </div>
    );
};

export default RolesPage; 